import UploadErrorDialog from '@/components/UploadErrorDialog/UploadErrorDialog'
import { Button } from '@/components/ui/Button'
import type React from 'react'
import { useCallback, useRef, useState } from 'react'
import Cropper from 'react-easy-crop'

import dropImg from '@/assets/drop_file_here.png'
import iconCrop from '@/assets/icons/icon_crop.svg'
import iconDelete from '@/assets/icons/icon_delete.svg'
import styles from './uploadLogo.module.css'

const ACCEPT = ['image/jpeg', 'image/png', 'image/svg+xml', 'image/webp']
const MAX_SIZE = 100 * 1024 * 1024

export type UploadLogoFormProps = {
  onBack: () => void
  onFinish: (file: File | null, dataUrl?: string) => void
}

export const RegistrationUploadLogoForm = ({ onBack, onFinish }: UploadLogoFormProps) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragOver, setDragOver] = useState(false)
  const [loading, setLoading] = useState(false)
  const [preview, setPreview] = useState<string | null>(null)
  const [currentFile, setCurrentFile] = useState<File | null>(null)
  const [showActions, setShowActions] = useState(false)
  const [errorText, setErrorText] = useState<string | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [isCropping, setIsCropping] = useState(false)
  const [crop, setCrop] = useState<{ x: number; y: number }>({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [minZoom, setMinZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<{
    width: number
    height: number
    x: number
    y: number
  } | null>(null)

  const validate = useCallback((file: File) => {
    if (!ACCEPT.includes(file.type)) return 'Only jpg, png, svg are allowed'
    if (file.size > MAX_SIZE) return 'File is too large (max 100MB)'
    return ''
  }, [])

  const openPicker = () => inputRef.current?.click()

  const readFile = useCallback(
    (file: File) => {
      const err = validate(file)
      if (err) {
        setErrorText(err)
        return
      }
      setLoading(true)
      const reader = new FileReader()
      reader.onload = () => {
        setPreview(reader.result as string)
        setCurrentFile(file)
        setLoading(false)
      }
      reader.onerror = () => setLoading(false)
      reader.readAsDataURL(file)
    },
    [validate],
  )

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setDragOver(false)
      const file = e.dataTransfer.files?.[0]
      if (file) readFile(file)
    },
    [readFile],
  )

  const onSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) readFile(file)
  }

  const onDelete = () => {
    setPreview(null)
    setCurrentFile(null)
    setIsCropping(false)
  }

  const enterCropMode = () => {
    if (!preview) return
    setIsCropping(true)
  }

  const onApplyCrop = async () => {
    if (!preview || !croppedAreaPixels) return
    const canvas = document.createElement('canvas')
    canvas.width = croppedAreaPixels.width
    canvas.height = croppedAreaPixels.height

    const ctx = canvas.getContext('2d')
    if (!ctx) return
    await new Promise<void>((resolve) => {
      const image = new Image()
      image.onload = () => {
        ctx.drawImage(
          image,
          croppedAreaPixels.x,
          croppedAreaPixels.y,
          croppedAreaPixels.width,
          croppedAreaPixels.height,
          0,
          0,
          croppedAreaPixels.width,
          croppedAreaPixels.height,
        )
        resolve()
      }
      image.src = preview
    })

    const data = canvas.toDataURL('image/png')
    setPreview(data)

    canvas.toBlob((blob) => {
      if (blob) {
        const croppedFile = new File([blob], currentFile?.name || 'cropped-logo.png', {
          type: 'image/png',
        })
        setCurrentFile(croppedFile)
      }
    }, 'image/png')

    setIsCropping(false)
  }

  const onCancelCrop = () => setIsCropping(false)

  return (
    <div className={styles.wrapper}>
      <UploadErrorDialog isOpen={!!errorText} onClose={() => setErrorText(null)} message={errorText || ''} />
      <div
        ref={containerRef}
        className={`${styles.drop} ${dragOver ? styles.dragOver : ''} ${preview && !isCropping ? styles.hasImage : ''}`}
        style={!isCropping && preview ? { backgroundImage: `url(${preview})` } : undefined}
        onDragOver={(e) => {
          e.preventDefault()
          setDragOver(true)
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onMouseEnter={() => setShowActions(true)}
        onMouseLeave={() => {
          setShowActions(false)
        }}
      >
        {!preview && !loading && (
          <>
            <img src={dropImg} alt="Drop here" className={styles.centerImg} />
            <div className={styles.flexRow}>
              <div className={styles.helper}>Drag and drop or</div>
              <button type="button" className={styles.uploadBtn} onClick={openPicker}>
                Upload File
              </button>
              <input ref={inputRef} type="file" accept={ACCEPT.join(',')} onChange={onSelect} style={{ display: 'none' }} />
            </div>
          </>
        )}
        {loading && (
          <div className={styles.loader} aria-live="polite">
            Loading...
          </div>
        )}
        {preview && !isCropping && showActions && (
          <div className={styles.actions}>
            <button type="button" className={styles.actionBtn} onClick={onDelete}>
              <img src={iconDelete} alt="" /> Delete
            </button>
            <button type="button" className={styles.actionBtn} onClick={enterCropMode}>
              <img src={iconCrop} alt="" /> Crop
            </button>
          </div>
        )}

        {preview && isCropping && (
          <div className={styles.cropOverlay}>
            <Cropper
              image={preview}
              crop={crop}
              zoom={zoom}
              aspect={1}
              onCropChange={setCrop}
              onCropComplete={(_croppedArea, pixels) => {
                setCroppedAreaPixels(pixels)
              }}
              minZoom={minZoom}
              onMediaLoaded={({ naturalWidth, naturalHeight }) => {
                const cropContainerEl = document.querySelector(`.${styles.cropContainer}`) as HTMLElement | null
                const container = cropContainerEl ?? containerRef.current
                if (!container) return
                const cw = container.clientWidth
                const ch = container.clientHeight
                const containerSize = Math.min(cw, ch)
                const scaleX = containerSize / naturalWidth
                const scaleY = containerSize / naturalHeight
                const needed = Math.max(scaleX, scaleY)
                const initialZoom = Math.max(1, needed)
                setMinZoom(initialZoom)
                setZoom(initialZoom)
              }}
              onZoomChange={setZoom}
            />
            <div className={styles.cropActions}>
              <input
                type="range"
                min={minZoom}
                max={3}
                step={0.1}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                aria-label="Zoom"
                style={{ width: 180 }}
              />
              <button type="button" className={styles.actionBtn} onClick={onCancelCrop}>
                Cancel
              </button>
              <button type="button" className={styles.actionBtn} onClick={onApplyCrop}>
                Apply
              </button>
            </div>
          </div>
        )}
      </div>

      <div className={styles.bottomRow}>
        <p className={styles.hint}>
          Supported files: <span style={{ color: '#151A1E' }}>.jpg, .png, .svg, .webp</span>
        </p>
        <p className={styles.hint}>
          Maximum file size: <span style={{ color: '#151A1E' }}>100MB</span>
        </p>
      </div>

      <div className={styles.bottomRow}>
        <p className={styles.hint}>
          Recommended image size: <span style={{ color: '#151A1E' }}>500x500px</span>
        </p>
        <p className={styles.hint}>
          Recommended image ratio: <span style={{ color: '#151A1E' }}>1:1</span>
        </p>
      </div>

      <div className={styles.buttons}>
        <Button type="button" variant="outline" size="large" style={{ width: '100%' }} className={styles.backBtn} onClick={onBack}>
          Back
        </Button>
        <Button
          type="button"
          variant="fill"
          size="large"
          style={{ width: '100%' }}
          className={styles.submitBtn}
          disabled={loading || !preview || !currentFile}
          onClick={() => onFinish(currentFile, preview || undefined)}
        >
          Next
        </Button>
      </div>
    </div>
  )
}
